import {
    autobind,
    connect,
    GameState,
    Components,
} from "macguffin"

import reducer from "./redux/reducer"
import sagas from "./redux/sagas"
import GameMode from "./modes"
import GameModeGroup from "./modes/GameModeGroup"
import Timeout from 'macguffin/Utils/Timeout'
import bowser from 'bowser'
import PropTypes from 'prop-types'

import { assetTypes } from 'macguffin/redux/constants'
const { asset, sound, font } = assetTypes
export * as SettingsPages from "./settingsPages"

const { fork } = GameState.SagaEffects

const { Component } = Components

const GAME_WIDTH = 1920
const GAME_HEIGHT = 1050

const REELS_RATIO = 5 / 3

const METER_SCALE = 0.085

import Background from "./components/background"
import CometWilds from "./components/cometWilds"
import JackpotMeter from "./components/jackpotMeter"
import MessageBox from "./components/messageBox"
import Mars from "./components/mars"
import PickStage from "./components/pickStage"
import ProgressionMeter from "./components/progressionMeter"
import Reels from "./components/reels"
import Rocket from "./components/rocket"
import ScatterMeter from "./components/scatterMeter"
import Winlines from "./components/winLines"
import WinSymbols from "./components/winSymbols"
import WinCounter from "./components/winCounter"
import FreespinsLeft from "./components/freespinsLeft"
import Fade from "./components/fade"
import BoosterStack from "./components/boosterStack"
import Countdown from "./components/countdown"
require('../css/main.less')


const ReelVisibleGroup = new GameModeGroup([
    GameMode.RESUME,
    GameMode.MAIN,
    GameMode.COMET_WILDS,
    GameMode.RESPIN,
    GameMode.FREESPIN,
    GameMode.REVERT
])
const SpaceGroup = new GameModeGroup()

const floor = Math.floor
function getReelsDimensions() {
    const mainContent = document.getElementById("mainContent")
    const mainWidth = mainContent ? mainContent.offsetWidth : 0
    const mainHeight = mainContent ? mainContent.offsetHeight : 0

    let reelsAndMeterWidth = mainWidth
    let reelsAndMeterHeight = floor(reelsAndMeterWidth / REELS_RATIO)

    if (reelsAndMeterHeight > mainHeight) {
        reelsAndMeterHeight = mainHeight
        reelsAndMeterWidth = floor(reelsAndMeterHeight * REELS_RATIO)
    }

    const meterWidth = reelsAndMeterWidth * METER_SCALE
    const meterHeight = reelsAndMeterHeight

    const reelsWidth = reelsAndMeterWidth * (1 - METER_SCALE)
    const reelsHeight = reelsAndMeterHeight

    const leftContent = document.getElementById("leftContent")
    const leftWidth = leftContent ? leftContent.offsetWidth : 0
    const leftHeight = leftContent ? leftContent.offsetHeight : 0

    const rightContent = document.getElementById("rightContent")
    const rightWidth = rightContent ? rightContent.offsetWidth : 0
    const rightHeight = rightContent ? rightContent.offsetHeight : 0

    return {
        mainWidth,
        mainHeight,
        reelsAndMeterWidth,
        reelsAndMeterHeight,
        reelsWidth,
        reelsHeight,
        meterWidth,
        meterHeight,
        leftWidth,
        leftHeight,
        rightWidth,
        rightHeight
    }
}

function getRocketDimensions() {
    const rocketContainer = document.getElementById("rocketContainer")
    const rocketWidth = rocketContainer ? rocketContainer.offsetWidth : 0
    const rocketHeight = rocketContainer ? rocketContainer.offsetHeight : 0

    return {
        rocketWidth,
        rocketHeight
    }
}

@connect(state => state)
export default class Game extends Component {
    static propTypes = {
        game: PropTypes.object
    }

    componentWillMount() {
        this.updateDimensions()
    }

    componentDidMount() {
        window.addEventListener("resize", this.doResize)
    }

    componentWillUnmount() {
        window.removeEventListener("resize", this.doResize)
    }

    doResize = () => {
        if (bowser.ios) {
            this.resizeTimeout && this.resizeTimeout.cancel()
            this.resizeTimeout = new Timeout(() => {
                this.updateDimensions()
            }, 200)
        } else {
            this.updateDimensions()
        }
    }

    updateDimensions = () => {
        const windowWidth = window.innerWidth
        const windowHeight = window.innerHeight
        let scale = 1
        const ratio = windowWidth / windowHeight

        scale = Math.min(windowWidth / GAME_WIDTH, windowHeight / GAME_HEIGHT)

        if (scale > 1) scale = 1

        const width = GAME_WIDTH * scale
        const height = GAME_HEIGHT * scale

        let dpr = window.devicePixelRatio || 1
        if (dpr > 1.5) dpr = 1.5

        this.setState({
            scale,
            ratio,
            width,
            height,
            windowWidth,
            windowHeight,
            dpr
        })
    }

    render() {
        const { props, state } = this
        const { game } = props
        const { currentGameMode } = game

        const preload = currentGameMode === GameMode.RESUME
        const showMars =
            currentGameMode === GameMode.MARS_INTRO ||
            currentGameMode === GameMode.JACKPOT_DRAW
        const showPick = currentGameMode === GameMode.PICK
        const showReels = ReelVisibleGroup.contains(currentGameMode)
        const showSpace =
            currentGameMode === GameMode.FREESPIN ||
            currentGameMode === GameMode.MARS_INTRO

        const renderProperties = {
            preload,
            showMars,
            showPick,
            showReels,
            showSpace
        }

        
        return (
            this.renderLandscape(renderProperties)
        )
    }

    renderLandscape(renderProperties) {
        const { props, state } = this
        const { dpr, windowWidth, windowHeight } = state
        const {
            preload,
            showReels,
            showPick,
            showMars,
            showSpace
        } = renderProperties

        const {
            mainWidth,
            mainHeight,
            reelsAndMeterWidth,
            reelsAndMeterHeight,
            reelsWidth,
            reelsHeight,
            meterWidth,
            meterHeight,
            leftWidth,
            leftHeight,
            rightWidth,
            rightHeight
        } = getReelsDimensions()

        const { rocketWidth, rocketHeight } = getRocketDimensions()
        const landscape = windowWidth > windowHeight

        const mainSlotStyle = {
            position: "relative",
            left: `${(mainWidth - reelsAndMeterWidth) * 0.5}px`
        }

        const reelVisibility = showReels ? "visible" : "hidden"
        const marsVisibility = showMars ? "visible" : "hidden"
        const pickVisibility = showPick ? "visible" : "hidden"

        return (
            <div key="gameContainer" id="gameContainer">
                <Background key="Background" />
                <div id="topContent" key="topContent">
                    <JackpotMeter
                        key="JackpotMeter"
                        width={mainWidth}
                        height={mainHeight}
                        logo={landscape ? "headerLandscape" : "headerPortrait"}
                    />
                </div>
                <Rocket
                    key="Rocket"
                    width={rocketWidth}
                    height={rocketHeight}
                    resolution={dpr}
                />
                {landscape &&
                    <div id="leftContent" key="leftContent">
                        <BoosterStack
                            key="BoosterStack"
                            width={leftWidth}
                            height={leftHeight}
                        />
                        <ProgressionMeter key="ProgressionMeter" />
                    </div>
                }
                <div id="mainContent" key="mainContent">
                    <div key="MainArea" style={mainSlotStyle}>
                        <ScatterMeter
                            key="ScatterMeter"
                            width={meterWidth}
                            height={meterHeight}
                        />
                        <ProgressionMeter
                            key="ProgressionMeter"
                            width={meterWidth}
                            height={meterHeight}
                            visibility={reelVisibility}
                        />
                        <Reels
                            key="Reels"
                            resolution={dpr}
                            left={meterWidth}
                            top={0}
                            width={reelsWidth}
                            height={reelsHeight}
                            visibility={reelVisibility}
                        />
                        <Winlines
                            key="Winlines"
                            resolution={dpr}
                            left={meterWidth}
                            top={0}
                            width={reelsWidth}
                            height={reelsHeight}
                            visibility={reelVisibility}
                        />
                        <CometWilds
                            key="CometWilds"
                            resolution={dpr}
                            left={meterWidth}
                            top={0}
                            width={reelsWidth}
                            height={reelsHeight}
                            visibility={reelVisibility}
                        />
                        <WinSymbols
                            key="WinSymbols"
                            resolution={dpr}
                            left={meterWidth}
                            top={0}
                            width={reelsWidth}
                            height={reelsHeight}
                            visibility={reelVisibility}
                        />
                        <WinCounter
                            key="WinCounter"
                            resolution={dpr}
                            left={meterWidth}
                            top={0}
                            width={reelsWidth}
                            height={reelsHeight}
                            visibility={reelVisibility}
                        />
                    </div>
                    <PickStage
                        key="PickStage"
                        left={mainSlotStyle.left}
                        width={reelsAndMeterWidth}
                        height={reelsAndMeterHeight}
                        visibility={pickVisibility}
                        showPick={showPick}
                    />
                </div>

                {!landscape && !showMars && (
                    <div id="bottomContent" key="bottomContent">
                        <MessageBox
                            key="MessageBox"
                            width={mainWidth}
                            height={mainHeight}
                        />
                    </div>
                )}


                { !landscape && 
                    <div id="leftContent" key="leftContent">
                        <BoosterStack
                            key="BoosterStack"
                            width={leftWidth}
                            height={leftHeight}
                        />
                    </div>
                }
                <FreespinsLeft
                        key="FreespinsLeft"
                        left={mainSlotStyle.left}
                        width={rightWidth}
                        height={rightHeight}
                    />

                <Countdown
                    key="Countdown"
                    width={mainWidth}
                    height={mainHeight}
                />
                <Mars
                    key="Mars"
                    width={reelsAndMeterWidth}
                    height={reelsAndMeterHeight}
                    visibility={marsVisibility}
                    showMars={showMars}
                />
                {(landscape || (!landscape && showMars)) &&
                    <div id="bottomContent" key="bottomContent">
                        <MessageBox
                            key="MessageBox"
                            width={mainWidth}
                            height={mainHeight}
                        />
                    </div>
                }
                <Fade key="Fade" />
            </div>
        )
    }
}

export const LoadingScreen = (
    <div className="loadingLogos">
        <div className="studioLogo" />
        <div className="gameLogo" />
    </div>
)

export const Assets = [
    { id: "loadingLogo", src: "assets/static/loading/loading_logo.png", type: asset },
    { id: "loadingMars", src: "assets/static/loading/loading_mars.png", type: asset },
    { id: "relaxLogo", src: "assets/static/loading/relax_logo.png", type: asset },
    { id: "splashScreenComet", src: "assets/static/splash/splash_comet.png", type: asset },
    { id: "splashScreenMars", src: "assets/static/splash/splash_mars.png", type: asset },
    { id: "earthBackground", src: "assets/static/backgrounds/background.png", type: asset },
    { id: "earthClouds", src: "assets/static/backgrounds/cloud.png", type: asset },
    { id: "earthCloudMotion", src: "assets/static/backgrounds/cloud_motion.jpg", type: asset },
    { id: "earthSky", src: "assets/static/backgrounds/sky.jpg", type: asset },
    { id: "planetEarth", src: "assets/static/backgrounds/earth.png", type: asset },
    { id: "greenGalaxy", src: "assets/static/backgrounds/light_green.png", type: asset },
    { id: "orangeGalaxy", src: "assets/static/backgrounds/light_orange.png", type: asset },
    { id: "pinkGalaxy", src: "assets/static/backgrounds/light_pink.png", type: asset },
    { id: "purpleGalaxy", src: "assets/static/backgrounds/light_purple.png", type: asset },
    { id: "stars1", src: "assets/static/backgrounds/star01.png", type: asset },
    { id: "stars2", src: "assets/static/backgrounds/star02.png", type: asset },
    { id: "stars3", src: "assets/static/backgrounds/star03.png", type: asset },
    { id: "planetMars", src: "assets/static/backgrounds/mars.png", type: asset },
    { id: "messageBoxBackground", src: "assets/static/backgrounds/text_bg.png", type: asset },
    { id: "winFeedback", src: "assets/static/winFeedback/wf_bg.png", type: asset },
    { id: "winlineBackground", src: "assets/static/winlines/winline_bg.png", type: asset },
    { id: "winlineDot", src: "assets/static/winlines/winline_dot.png", type: asset },
    { id: "headerPortrait", src: "assets/static/header/header_portrait.png", type: asset },
    { id: "headerLandscape", src: "assets/static/header/header_landscape.png", type: asset },
    { id: "pickScanningBar", src: "assets/static/pickMode/pm_bg_detail_scanningbar.png", type: asset },
    { id: "pickFrame", src: "assets/static/pickMode/pm_bg_detail.png", type: asset },
    { id: "pickBackground", src: "assets/static/pickMode/pm_bg.png", type: asset },
    { id: "pickBlueBox", src: "assets/static/pickMode/pm_blue_box.png", type: asset },
    { id: "pickBlueNormal", src: "assets/static/pickMode/pm_blue_normal.png", type: asset },
    { id: "pickGreenBox", src: "assets/static/pickMode/pm_green_box.png", type: asset },
    { id: "pickGreenNormal", src: "assets/static/pickMode/pm_green_normal.png", type: asset },
    { id: "pickHeader", src: "assets/static/pickMode/pm_header_textbox.png", type: asset },
    { id: "pickOrangeBox", src: "assets/static/pickMode/pm_orange_box.png", type: asset },
    { id: "pickOrangeNormal", src: "assets/static/pickMode/pm_orange_normal.png", type: asset },
    { id: "pickPinkBox", src: "assets/static/pickMode/pm_pink_box.png", type: asset },
    { id: "pickPinkNormal", src: "assets/static/pickMode/pm_pink_normal.png", type: asset },
    { id: "pickStage1Background", src: "assets/static/pickMode/pm_stage1_bg.png", type: asset },
    { id: "pickStage1Blue", src: "assets/static/pickMode/pm_stage1_blue.png", type: asset },
    { id: "pickStage1Green", src: "assets/static/pickMode/pm_stage1_green.png", type: asset },
    { id: "pickStage1Orange", src: "assets/static/pickMode/pm_stage1_orange.png", type: asset },
    { id: "pickStage1Pink", src: "assets/static/pickMode/pm_stage1_pink.png", type: asset },
    { id: "pickStage2Background", src: "assets/static/pickMode/pm_stage2_bg.png", type: asset },
    { id: "pickStage2Blue", src: "assets/static/pickMode/pm_stage2_blue.png", type: asset },
    { id: "pickStage2Green", src: "assets/static/pickMode/pm_stage2_green.png", type: asset },
    { id: "pickStage2Orange", src: "assets/static/pickMode/pm_stage2_orange.png", type: asset },
    { id: "pickStage2Pink", src: "assets/static/pickMode/pm_stage2_pink.png", type: asset },
    { id: "pickStage3Background", src: "assets/static/pickMode/pm_stage3_bg.png", type: asset },
    { id: "pickStage3Blue", src: "assets/static/pickMode/pm_stage3_blue.png", type: asset },
    { id: "pickStage3Green", src: "assets/static/pickMode/pm_stage3_green.png", type: asset },
    { id: "pickStage3Orange", src: "assets/static/pickMode/pm_stage3_orange.png", type: asset },
    { id: "pickStage3Pink", src: "assets/static/pickMode/pm_stage3_pink.png", type: asset },
    { id: "freespinCounterBackground", src: "assets/static/pickMode/pm_sum_freespinbg.png", type: asset },
    { id: "pickBoosterBackground", src: "assets/static/pickMode/pm_sum_itembg.png", type: asset },
    { id: "scatterMeter", src: "assets/static/scatter/Reel_scbar.png", type: asset },
    { id: "scatterSymbol", src: "assets/static/scatter/SC.png", type: asset },
    { id: "scatterMeterGlow", src: "assets/static/scatter/scbar_glow.png", type: asset },
    { id: "scatterMeterLevel", src: "assets/static/scatter/scbar_on.png", type: asset },
    { id: "progressionMeterDot", src: "assets/static/progression/dot.png", type: asset },
    { id: "progressionMeter", src: "assets/static/progression/Reel_pgbar.png", type: asset },
    { id: "marsBackground", src: "assets/static/jackpotDraw/jpsum_bg.jpg", type: asset },
    { id: "landerLegs", src: "assets/static/jackpotDraw/jpsum_rocket_legs.png", type: asset },
    { id: "landerBody", src: "assets/static/jackpotDraw/jpsum_rocket.png", type: asset },
    { id: "marsSmoke1", src: "assets/static/jackpotDraw/jpsum_smoke01.png", type: asset },
    { id: "marsSmoke2", src: "assets/static/jackpotDraw/jpsum_smoke02.png", type: asset },
    { id: "marsSummaryJackpot", src: "assets/static/jackpotDraw/jpsum_text_jackpot.png", type: asset },
    { id: "marsSummaryPanel", src: "assets/static/jackpotDraw/jpsum_textbox.png", type: asset },
    { id: "marsBlueArrow", src: "assets/static/jackpotDraw/pm_blue_arrow.png", type: asset },
    { id: "marsBlueRing", src: "assets/static/jackpotDraw/pm_blue_ring.png", type: asset },
    { id: "marsGreenArrow", src: "assets/static/jackpotDraw/pm_green_arrow.png", type: asset },
    { id: "marsGreenRing", src: "assets/static/jackpotDraw/pm_green_ring.png", type: asset },
    { id: "marsOrangeArrow", src: "assets/static/jackpotDraw/pm_orange_arrow.png", type: asset },
    { id: "marsOrangeRing", src: "assets/static/jackpotDraw/pm_orange_ring.png", type: asset },
    { id: "marsPinkArrow", src: "assets/static/jackpotDraw/pm_pink_arrow.png", type: asset },
    { id: "marsPinkRing", src: "assets/static/jackpotDraw/pm_pink_ring.png", type: asset },
    { id: "rulesBox", src: "assets/static/rules/rules_box.png", type: asset },
    { id: "rulesBoosters", src: "assets/static/rules/rules_buffs.png", type: asset },
    { id: "rulesScatter", src: "assets/static/rules/rules_mars.png", type: asset },
    { id: "rulesWild", src: "assets/static/rules/rules_wild.png", type: asset },

    // ATLAS
    { id: "reel-atlas", src: "assets/reel-atlas.json", type: asset },
    { id: "rocket-atlas", src: "assets/rocket-atlas.json", type: asset },
    { id: "winning-symbols-high", src: "assets/winningSymbols/high/H.json", type: asset },
    { id: "winning-symbols-medium", src: "assets/winningSymbols/medium/M.json", type: asset },
    { id: "winning-symbols-low", src: "assets/winningSymbols/low/L.json", type: asset },
    { id: "winning-symbols-wild", src: "assets/winningSymbols/wild/wild.json", type: asset },
    { id: "winning-symbols-glow", src: "assets/winningSymbols/high/H_glow.json", type: asset },
    { id: "winning-symbols-star", src: "assets/winningSymbols/high/H_star.json", type: asset },
    { id: "meteor", src: "assets/meteor/meteor_spine.json", type: asset },
    { id: "win-feedback", src: "assets/winFeedback/winfeedback.json", type: asset },

    //MISC
    { id: "badge", src: "assets/badge.png", type: asset },
    { id: "wildText", src: "assets/wild_text.png", type: asset },
    { id: "flags", src: "assets/flags.png", type: asset },

    //FONTS
    { id: "impact", src: "assets/fonts/impact.ttf", type: font },
    { id: "good times rg", src: "assets/fonts/good times rg.ttf", type: font },
    { id: "WorkSans-Bold", src: "assets/fonts/WorkSans-Bold.ttf", type: font },

    //SOUNDS
    { src: "assets/sounds/sfxsprite.json", format: "map", type: sound },
    { src: "assets/sounds/musicsprite.json", format: "map", type: sound }
]

export function* Sagas() {
    yield fork(sagas)
}

export const Reducer = reducer
